import { ActivityTypeSchema, FormFieldsSchema, MealTypeSchema, RMRMethodSchema, SexTypeSchema } from "@notemeal/validators";
import { z } from "zod";

export const v10 = "V1";
export const v11 = "V1.1";
export const v21 = "V2.1";
export const v30 = "V3.0";
export const v31 = "V3.1";
export const latestVersion = v31;
export type PendingStateVersion = typeof v31 | typeof v30 | typeof v21 | typeof v11 | typeof v10;

// V3.0 MacroProtocal removes showInKg

export const MealOptionSchema = z.object({
  id: z.string().uuid(),
  note: z.string().nullable(),
  name: z.string().nullable(),
  position: z.number().gte(0),
  servingAmounts: z
    .object({
      amount: z.number(),
      position: z.number().gte(0),
      servingId: z.string().uuid(),
    })
    .array(),
});

export const MealSchema = z.object({
  id: z.string().uuid(),
  choRatio: z.number(),
  choRatioInput: z.string(),
  fatRatio: z.number(),
  fatRatioInput: z.string(),
  meal: z.object({
    name: z.string(),
    type: MealTypeSchema,
    start: z.string(),
    end: z.string(),
  }),
  mealOptions: MealOptionSchema.array(),
  proRatio: z.number(),
  proRatioInput: z.string(),
});

export const FormFieldsAnthroSchema = z.object({
  __typename: z.literal("FormFields"),
  formFields: FormFieldsSchema.array(),
  sampleWeight: z.number(),
  sampleWeightInput: z.string(),
  samplePercentBodyFat: z.number().nullable(),
  samplePercentBodyFatInput: z.string(),
  sampleHeight: z.number().nullable(),
  sampleHeightInput: z.string(),
  sampleAge: z.number().nullable(),
  sampleAgeInput: z.string(),
  sampleSex: SexTypeSchema,
});

export const TemplateAnthroSchema = z.object({
  __typename: z.literal("Template"),
  height: z.number().nullable(),
  heightInput: z.string(),
  percentBodyFat: z.number().nullable(),
  percentBodyFatInput: z.string(),
  weight: z.number(),
  weightInput: z.string(),
  sampleAge: z.number().nullable(),
  sampleAgeInput: z.string(),
  sampleSex: SexTypeSchema,
});

export const AnthroStateSchema = z.union([FormFieldsAnthroSchema, TemplateAnthroSchema]);

export const MacroStateSchema = z.object({
  percentInput: z.string(),
  gPerKGInput: z.string(),
  percent: z.number(),
  gPerKg: z.number(),
  usesPercent: z.boolean(),
});

export const MealPlanTemplateGoalStateSchema = z.object({
  __typename: z.literal("MealPlanTemplateGoalState"),
  goalTypeId: z.string(),
  id: z.string().uuid(),
  kcalOffset: z.number(),
  kcalOffsetInput: z.string(),
  name: z.string(),
});

export const calorieBudgetPayload = z.object({
  rmrMethod: RMRMethodSchema,
  activityFactor: z.number(),
  activityFactorInput: z.string(),
  goals: MealPlanTemplateGoalStateSchema.array(),
  selectedPreviewGoal: MealPlanTemplateGoalStateSchema.nullable(),
});

export const MacroProtocolSchema = z.object({
  cho: MacroStateSchema,
  pro: MacroStateSchema,
  fat: MacroStateSchema,
  useExistingAnthropometry: z.boolean(),
  anthropometry: AnthroStateSchema,
  calorieBudget: calorieBudgetPayload,
  usingCalorieBudget: z.boolean(),
});

export const ActivitySchema = z.object({
  id: z.string().uuid(),
  end: z.string(),
  name: z.string(),
  start: z.string(),
  type: ActivityTypeSchema,
});

export const IdAndNameSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const FoodPreferencesSchema = z.object({
  promotedFoods: IdAndNameSchema.array(),
  avoidedFoods: IdAndNameSchema.array(),
  promotedFoodGroups: IdAndNameSchema.array(),
  avoidedFoodGroups: IdAndNameSchema.array(),
});

export const MacroMealPlanTemplateEditStateSchemaV30 = z.object({
  version: z.literal(v30),
  __typename: z.literal("Edit"),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  macroProtocol: MacroProtocolSchema,
  meals: MealSchema.array(),
  activities: ActivitySchema.array(),
  foodPreferences: FoodPreferencesSchema,
});

// TODO: metric update run phase - macroProtocol.anthro to metric variable names
// TODO: metric update run phase - update mpt => macro => anthro nested states/resolvers
export const MacroMealPlanTemplateEditStateSchemaV31 = z.object({
  version: z.literal(latestVersion),
  __typename: z.literal("Edit"),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  macroProtocol: MacroProtocolSchema,
  meals: MealSchema.array(),
  activities: ActivitySchema.array(),
  foodPreferences: FoodPreferencesSchema,
});
